/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import { APP_BASEMAPS } from '../../theme';
import * as actions from './actions';
import initialState, { INITIAL_VIEW_PORT } from './initial-state';

export default {
  [actions.setInitialUrlCoordinates]: (state, { payload }) => ({
    ...state,
    initialUrlCoordinates: payload,
  }),
  [actions.setMap]: (state, { payload }) => ({ ...state, ...payload }),
  [actions.setMapBounds]: (state, { payload }) => ({
    ...state,
    bounds: payload,
  }),
  [actions.setLocationHighlight]: (state, { payload }) => {
    const { id, geojson } = payload;
    return {
      ...state,
      locationHighlight: {
        key: `bounds-${id}`,
        id: `bounds-${id}`,
        slug: `bounds-${id}`,
        name: 'Bounds',
        zIndex: 2000,
        provider: 'geojson',
        type: 'geojson',
        source: {
          type: 'geojson',
          data: geojson,
        },
        render: {
          metadata: {
            position: 'top',
          },
          layers: [
            {
              id: `${id}-fill`,
              type: 'fill',
              source: id,
              paint: {
                'fill-color': 'transparent',
                'fill-opacity': 0.25,
              },
            },
            {
              id: `${id}-line2`,
              type: 'line',
              source: id,
              paint: {
                'line-color': '#FFFFFF',
                'line-width': 5,
              },
            },
            {
              id: `${id}-line`,
              type: 'line',
              source: id,
              paint: {
                'line-color': '#000000',
                'line-width': 3,
                'line-dasharray': [5, 1, 1, 1, 1, 1],
              },
            },
          ],
        },
      },
    };
  },
  [actions.setMapStyle]: (state, { payload }) => {
    const validBaseMap = APP_BASEMAPS.find((x) => x.slug === payload) || APP_BASEMAPS[0];

    return {
      ...state,
      mapStyle: validBaseMap.slug,
    };
  },
  [actions.setMapLabels]: (state, { payload }) => ({
    ...state,
    mapLabels: payload,
  }),
  [actions.setMapRoads]: (state, { payload }) => ({
    ...state,
    mapRoads: payload,
  }),
  [actions.setMapViewport]: (state, { payload }) => ({
    ...state,
    viewport: payload,
  }),
  [actions.setMapInteractions]: (state, { payload }) => {
    const { features = [], lngLat = [] } = payload;

    const interactions = features.reduce(
      (obj, next) => ({
        ...obj,
        [next.layer.source]: {
          id: next.id,
          data: next.properties,
          geometry: next.geometry,
        },
      }),
      {}
    );

    return {
      ...state,
      latlng: {
        lat: lngLat[1],
        lng: lngLat[0],
      },
      interactions,
    };
  },
  [actions.resetLocationHighlight]: (state) => {
    return {
      ...state,
      locationHighlight: false,
    };
  },
  [actions.resetMap]: (state) => {
    const { mapStyle, mapLabels, mapRoads } = state;

    return {
      ...initialState,
      viewport: INITIAL_VIEW_PORT,
      mapStyle: mapStyle || initialState.mapStyle,
      mapLabels,
      mapRoads,
    };
  },
};
